/**
 * How the app reaches the API, and the desktop shell's native extras.
 *
 * The site is served from the same origin as the API, so it addresses it with
 * a relative path and authenticates with an HttpOnly cookie the browser
 * attaches on its own. The desktop app is a window onto that same site
 * (packages/desktop), so it does exactly the same: same origin, same cookie,
 * no token. What `isDesktop` still decides is only what a browser tab cannot
 * do — OS notifications, the dock badge, saving into Downloads.
 */

import { reportNativeNotificationOutcome } from "./native-notification-health.js";

/** Commands the desktop shell exposes. See packages/desktop/src-tauri/src/lib.rs. */
type DesktopBridge = {
  invoke<T>(
    command: string,
    args?: Record<string, unknown> | Uint8Array,
    options?: { headers?: Record<string, string> },
  ): Promise<T>;
};

declare global {
  interface Window {
    __TAURI_INTERNALS__?: { invoke: DesktopBridge["invoke"] };
  }
}

/**
 * Whether the page is running inside the desktop shell, which injects
 * `__TAURI_INTERNALS__` into the pages it loads.
 *
 * Read once at module load: it cannot change during a session, and a function
 * that re-checks would invite callers to branch on it repeatedly.
 */
export const isDesktop: boolean =
  typeof window !== "undefined" && window.__TAURI_INTERNALS__ !== undefined;

function invoke<T>(
  command: string,
  args?: Record<string, unknown> | Uint8Array,
  options?: { headers?: Record<string, string> },
): Promise<T> {
  const internals = window.__TAURI_INTERNALS__;
  if (!internals) throw new Error(`"${command}" is only available in the desktop app`);
  return internals.invoke<T>(command, args, options);
}

/**
 * The websocket endpoint: the page's own host, with a ws/wss scheme.
 *
 * Carries the active office as `?workspace=`, since a handshake cannot carry
 * the `x-workspace-id` header. Read on every (re)connect, and switching office
 * reloads the app, so the socket always opens on the office being shown.
 * Without it the server falls back to the viewer's default office.
 */
export function socketUrl(): string {
  const workspaceId = activeWorkspaceHeader()["x-workspace-id"];
  const query = workspaceId ? `?workspace=${encodeURIComponent(workspaceId)}` : "";
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/api/v1/realtime${query}`;
}

/** localStorage key holding the id of the office the person has switched to. */
export const ACTIVE_WORKSPACE_KEY = "astrum.workspace";

/**
 * The active-office header sent on every request.
 *
 * Read straight from localStorage so it is available to the low-level transport
 * without threading React context through fetch. Absent on first load (before
 * the switcher has resolved an office); the server then falls back to the
 * viewer's default office, so an empty header is safe. Storage access is
 * wrapped because a locked-down browser can throw on read.
 */
export function activeWorkspaceHeader(): Record<string, string> {
  try {
    const id = localStorage.getItem(ACTIVE_WORKSPACE_KEY);
    return id ? { "x-workspace-id": id } : {};
  } catch {
    return {};
  }
}

/* ───────────────────────── native affordances ───────────────────────── */

/**
 * Raise an OS notification.
 *
 * Whether something deserves one is decided by the caller, which knows what is
 * on screen; this only carries the request across to the shell. A no-op on the
 * web, where the notification centre in the shell is the whole story and a
 * second, browser-level popup would say the same thing twice.
 *
 * A single failure here — permission refused, or a notification daemon that
 * is not running — is not worth interrupting anything for; the message is
 * still in the in-app panel, so this never throws. But a *consistent* failure
 * means the user's popups are silently not happening at all, which is worth
 * telling them about — see native-notification-health.ts, which the outcome
 * is reported to either way.
 */
export async function notifyNative(
  title: string,
  body: string | null,
  destination: string | null,
): Promise<void> {
  if (!isDesktop || !desktopPopupsEnabled()) return;
  try {
    await invoke("notify", { title, body, destination });
    reportNativeNotificationOutcome(true);
  } catch {
    reportNativeNotificationOutcome(false);
  }
}

const DESKTOP_POPUPS_KEY = "astrum.desktop.popups";

/**
 * Whether the desktop app should raise OS popups at all — the app's own
 * switch, set in Profile, separate from the OS permission underneath it.
 * On unless turned off: popups are the reason to run the app in the tray.
 */
export function desktopPopupsEnabled(): boolean {
  try {
    return localStorage.getItem(DESKTOP_POPUPS_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setDesktopPopupsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(DESKTOP_POPUPS_KEY, enabled ? "on" : "off");
  } catch {
    // Best effort — a storage failure leaves popups at their default, on.
  }
}

/**
 * Raise one popup on request, so somebody can see for themselves whether the
 * OS is letting them through. Rejects, unlike `notifyNative`: here the
 * failure is the answer the person asked for.
 */
export async function sendTestNotification(): Promise<void> {
  if (!isDesktop) return;
  await invoke("notify", {
    title: "Astrum Connect",
    body: "Notifications are working. New messages will appear like this.",
    destination: null,
  });
}

/** Jump to the OS page that decides whether this app may notify at all. */
export async function openNotificationSettings(): Promise<void> {
  if (!isDesktop) return;
  await invoke("open_notification_settings");
}

export type AvailableUpdate = { version: string; notes: string | null };

/**
 * Whether a newer build is available.
 *
 * Rejects when the check itself failed, which the caller reports differently
 * from "up to date" — telling somebody they are current when the check never
 * completed is the one answer that is actually misleading.
 */
export async function checkForUpdate(): Promise<AvailableUpdate | null> {
  if (!isDesktop) return null;
  return invoke<AvailableUpdate | null>("check_for_update");
}

/** Download, install, and restart into the new version. */
export async function installUpdate(): Promise<void> {
  if (!isDesktop) return;
  await invoke("install_update");
}

/** Whether the app is set to start with the computer. False on the web. */
export async function isAutostartEnabled(): Promise<boolean> {
  if (!isDesktop) return false;
  try {
    return await invoke<boolean>("autostart_enabled");
  } catch {
    return false;
  }
}

/** Turn launch-at-login on or off. Rejects so the caller can report failure —
 *  unlike the badge, this one is a setting somebody deliberately changed. */
export async function setAutostart(enabled: boolean): Promise<void> {
  if (!isDesktop) return;
  await invoke("set_autostart", { enabled });
}

/**
 * Whether a launch at login opens to the tray rather than a window. Null when
 * the shell cannot say — the site can be newer than the installed app, and a
 * build from before the setting existed has no command to ask.
 */
export async function isStartMinimisedEnabled(): Promise<boolean | null> {
  if (!isDesktop) return null;
  try {
    return await invoke<boolean>("start_minimised_enabled");
  } catch {
    return null;
  }
}

export async function setStartMinimised(enabled: boolean): Promise<void> {
  if (!isDesktop) return;
  await invoke("set_start_minimised", { enabled });
}

/** Show the unread count on the dock or taskbar. Zero clears it. */
export async function setNativeBadge(count: number): Promise<void> {
  if (!isDesktop) return;
  try {
    await invoke("set_badge", { count: Math.max(0, Math.trunc(count)) });
  } catch {
    // Badges are decoration; failing to set one should never surface.
  }
}

/**
 * Where the notification the user just clicked was pointing, if any.
 *
 * The OS reports that a notification was activated but not which one, so the
 * shell holds the destination and this claims it. Returns null when the window
 * was focused for any other reason — which is most of the time.
 */
export async function takePendingDestination(): Promise<string | null> {
  if (!isDesktop) return null;
  try {
    return await invoke<string | null>("take_pending_destination");
  } catch {
    return null;
  }
}

/**
 * The URL for a file the API serves — the path itself, relative to the page's
 * own origin in the browser and the desktop app alike, so `<img src>` and
 * downloads carry the session cookie with nothing added.
 */
export function fileUrl(path: string): string {
  return path;
}

/**
 * A multipart upload.
 *
 * Kept out of the `api` helper because that one JSON-encodes its body, and a
 * multipart request needs the browser to set its own boundary header. Every
 * uploader in the app goes through here so the office header is applied in
 * one place rather than five that can drift apart.
 */
export async function uploadFile(path: string, form: FormData): Promise<Response> {
  return fetch(fileUrl(`/api/v1${path}`), {
    method: "POST",
    body: form,
    credentials: "include",
    headers: activeWorkspaceHeader(),
  });
}

/** A parsed upload result, mirroring the shape callers read off `fetch`'s Response. */
export interface UploadResult {
  ok: boolean;
  status: number;
  body: unknown;
  /** True when the response was not JSON (e.g. an HTML error page from a proxy or WAF). */
  nonJson?: boolean;
}

/** Read a fetch `Response` into an `UploadResult`, tolerating non-JSON bodies. */
export async function readUploadResponse(response: Response): Promise<UploadResult> {
  const text = await response.text().catch(() => "");
  try {
    return { ok: response.ok, status: response.status, body: JSON.parse(text) };
  } catch {
    return { ok: response.ok, status: response.status, body: {}, nonJson: true };
  }
}

/**
 * Upload a file and report progress as it goes.
 *
 * `fetch` cannot surface upload progress, so this uses XMLHttpRequest, whose
 * `upload.onprogress` gives bytes-sent while the body is still streaming. The
 * session cookie rides along via `withCredentials`. `onProgress` receives a 0..1 fraction; it may be called many
 * times, and once with 1 when the body finishes uploading (the server's own
 * processing — scan, store — happens after that, so 100% means "sent", not
 * "done", which is why the bubble shows a brief indeterminate tail).
 *
 * Returns a cancel function alongside the promise so a caller can abort an
 * in-flight upload (e.g. the user removes the pending bubble).
 */
export function uploadFileWithProgress(
  path: string,
  form: FormData,
  onProgress: (fraction: number) => void,
): { promise: Promise<UploadResult>; cancel: () => void } {
  const xhr = new XMLHttpRequest();

  const promise = (async (): Promise<UploadResult> => {
    const headers = activeWorkspaceHeader();

    return new Promise<UploadResult>((resolve, reject) => {
      xhr.open("POST", fileUrl(`/api/v1${path}`), true);
      xhr.withCredentials = true;
      for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      };
      // Body fully sent; the server is now scanning/storing.
      xhr.upload.onload = () => onProgress(1);

      xhr.onload = () => {
        let body: unknown = {};
        let nonJson = false;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          // Not JSON — typically an HTML error page from the proxy/WAF. The
          // caller words the failure from the status instead.
          nonJson = true;
        }
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, body, nonJson });
      };
      xhr.onerror = () => reject(new Error("network error during upload"));
      xhr.onabort = () => reject(new DOMException("upload cancelled", "AbortError"));

      xhr.send(form);
    });
  })();

  return { promise, cancel: () => xhr.abort() };
}

/**
 * Save bytes the app already holds as a named file.
 *
 * In a browser that is a click on a temporary `<a download>`. The desktop
 * webview has no download manager to receive that click — WKWebView drops it
 * without a trace — so the bytes go to Rust, which writes them into Downloads
 * and selects the file in Finder / Explorer. See downloads.rs.
 */
export async function saveBlob(blob: Blob, fileName: string): Promise<void> {
  if (isDesktop) {
    await invoke("save_download", new Uint8Array(await blob.arrayBuffer()), {
      headers: { "x-file-name": encodeURIComponent(fileName) },
    });
    return;
  }

  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = fileName;
  anchor.click();
  // Revoked on the next task, not synchronously: some browsers start reading
  // the blob only after the click handler returns.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
}

/**
 * Start a download.
 *
 * On the web, navigating to the URL lets the browser stream the file straight
 * to disk. The desktop webview has no download manager to hand that to, so the
 * bytes are fetched (the cookie attaches as on any same-origin request) and
 * saved through {@link saveBlob} instead.
 */
export async function downloadFile(path: string, fileName: string): Promise<void> {
  const url = fileUrl(path);

  if (!isDesktop) {
    window.location.assign(url);
    return;
  }

  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) throw new Error(`Download failed (${response.status})`);
  await saveBlob(await response.blob(), fileName);
}

/**
 * Download several files as one archive.
 *
 * Unlike {@link downloadFile}, this can't be a bare navigation on the web: the
 * id list travels in a POST body, so on both web and desktop the bytes are
 * fetched (the session cookie attaches on its own) and saved
 * through {@link saveBlob}. The server replies with a .zip named by date, but
 * the Content-Disposition filename only takes effect on a navigation, so the
 * name is supplied here.
 */
export async function downloadArchive(path: string, fileIds: string[], fileName: string): Promise<void> {
  const response = await fetch(fileUrl(`/api/v1${path}`), {
    method: "POST",
    headers: { "content-type": "application/json", ...activeWorkspaceHeader() },
    credentials: "include",
    body: JSON.stringify({ fileIds }),
  });
  if (!response.ok) throw new Error(`Download failed (${response.status})`);
  await saveBlob(await response.blob(), fileName);
}

/**
 * Open a protected file for viewing — a click on a chat image or attachment,
 * not a save.
 *
 * On the web that is a new tab. The desktop webview has no tabs, so there the
 * bytes are fetched and handed to Rust to open in the file's default
 * application — a spreadsheet opens in Excel, as it would from Finder.
 */
export async function openFile(path: string, fileName: string): Promise<void> {
  const url = fileUrl(path);

  if (!isDesktop) {
    window.open(url, "_blank", "noopener");
    return;
  }

  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) throw new Error(`Could not open file (${response.status})`);
  await invoke("open_download", new Uint8Array(await (await response.blob()).arrayBuffer()), {
    headers: { "x-file-name": encodeURIComponent(fileName) },
  });
}
